/**
 * OsInputRequestCard — surface B (PRD-005 / ARCH-028, IR-T-08).
 *
 * The OS owner card, rendered inside the /os owner status surface above the
 * status component. Slim warm OS register from design-final "Input Request - OS
 * Card.html": a white card with a navy->rose hairline at the top, the
 * "Eric, Yes Please Studio" from-line, a serif title + intro, then the shared
 * <InputRequestForm>. States (COPY §B): open / submitted / closed.
 *
 * EMPTY = the card is simply absent: this component returns null when there is
 * no request, so the OS status surface renders unchanged (IR-T-08 AC2). The
 * caller fetches the request (lib.fetchOsInputRequest) and renders this only
 * for an OS-context request.
 *
 * No new owner-write path beyond the RPC (IR-T-08 AC3): the only mutation is the
 * form's submit_input_response call.
 */

import { useState } from 'react';
import {
  AnsweredView,
  InputRequestForm,
} from './InputRequestForm';
import { EricAvatar } from './chrome';
import { sentWhen, type RecipientInputRequest } from './lib';

function OverlineRule(): JSX.Element {
  return (
    <div className="mb-[18px] flex items-center gap-2.5">
      <span className="font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-text-mute)]">
        From your engagement
      </span>
      <span className="h-px flex-1 bg-[color:var(--color-rule)]" />
    </div>
  );
}

function OpenState({
  request,
  onSubmitted,
}: {
  request: RecipientInputRequest;
  onSubmitted: () => void;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-2xl border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] shadow-[0_1px_2px_rgba(40,30,20,0.04),0_14px_40px_rgba(40,30,20,0.06)]">
      <div className="h-1 bg-gradient-to-r from-[color:var(--color-navy)] to-[color:var(--color-rose)]" />
      <div className="px-[clamp(24px,4vw,44px)] py-[clamp(28px,4vw,40px)]">
        <div className="mb-6 flex items-center gap-3">
          <EricAvatar size={30} />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-[color:var(--color-black)]">
              Eric, Yes Please Studio
            </div>
            <div className="text-[12px] text-[color:var(--color-gray-300)]">
              sent {sentWhen(request.created_at)} &middot; just for you
            </div>
          </div>
          <span className="ml-auto font-semibold text-[10px] uppercase tracking-[0.14em] text-[color:var(--color-rose-dark)]">
            A few questions
          </span>
        </div>

        <h2 className="mb-3.5 max-w-[560px] font-display text-[clamp(30px,4vw,42px)] italic leading-[1.08] tracking-[-0.01em] text-[color:var(--color-black)] text-pretty">
          {request.title}.
        </h2>
        {request.intro ? (
          <p className="mb-[clamp(28px,4vw,40px)] max-w-[540px] text-[clamp(15px,1.6vw,17px)] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
            {request.intro}
          </p>
        ) : (
          <div className="h-[clamp(20px,3vw,28px)]" />
        )}

        <InputRequestForm
          request={request}
          helperRegister="os"
          onSubmitted={onSubmitted}
        />
      </div>
    </section>
  );
}

function SubmittedState({
  request,
}: {
  request: RecipientInputRequest;
}): JSX.Element {
  const when = request.answered_at ? sentWhen(request.answered_at) : 'just now';
  return (
    <section className="overflow-hidden rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-white)]">
      <div className="flex items-center gap-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-linen)] px-[clamp(24px,4vw,44px)] py-[18px]">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-green-accent)] text-[12px] text-[color:var(--color-white)]"
          aria-hidden="true"
        >
          &#10003;
        </span>
        <div className="text-[14px] font-semibold text-[color:var(--color-black)]">
          Sent to Eric
        </div>
        <span className="ml-auto text-[13px] text-[color:var(--color-gray-300)]">
          {when}
        </span>
      </div>
      <div className="px-[clamp(24px,4vw,44px)] py-[clamp(28px,4vw,40px)]">
        <h2 className="mb-2 font-display text-[clamp(26px,3.2vw,34px)] italic leading-tight text-[color:var(--color-black)]">
          That&apos;s everything he needed.
        </h2>
        <p className="mb-[clamp(26px,3.5vw,34px)] max-w-[500px] text-[15px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
          Your answers are with Eric. Nothing else to do here. He&apos;ll pick
          it up from his side and carry it into the session.
        </p>
        <AnsweredView request={request} />
      </div>
    </section>
  );
}

function ClosedState({
  request,
}: {
  request: RecipientInputRequest;
}): JSX.Element {
  // A request the owner already answered, then Eric closed, still shows the
  // answers. A close that arrives while the request was open shows the closed
  // notice (COPY §B closed-while-open). We branch on answered_at.
  if (request.answered_at) {
    return <SubmittedState request={request} />;
  }
  return (
    <section className="rounded-2xl border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] px-[clamp(24px,4vw,44px)] py-[clamp(30px,4vw,44px)]">
      <div className="mb-4 flex items-center gap-3">
        <EricAvatar size={30} />
        <div className="text-[13px] font-semibold text-[color:var(--color-black)]">
          Eric, Yes Please Studio
        </div>
      </div>
      <h2 className="mb-2.5 max-w-[520px] font-display text-[clamp(26px,3.2vw,34px)] italic leading-tight text-[color:var(--color-black)] text-pretty">
        Eric closed this set of questions.
      </h2>
      <p className="max-w-[480px] text-[16px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
        Nothing more is needed from you here. If something still comes to mind,
        you can always raise it with him directly.
      </p>
    </section>
  );
}

/**
 * The OS owner card. Returns null when there is no request (empty = absent),
 * so the OS status surface is unchanged. The caller passes an OS-context
 * request (or null) it fetched with lib.fetchOsInputRequest.
 */
export function OsInputRequestCard({
  request,
}: {
  request: RecipientInputRequest | null;
}): JSX.Element | null {
  // A successful submit flips local state to the answered view immediately,
  // without a re-fetch (the surface stays a single calm affordance).
  const [submitted, setSubmitted] = useState(false);

  if (!request) return null;

  const effectiveStatus =
    submitted && request.status === 'open' ? 'answered' : request.status;

  return (
    <div className="mb-8">
      <OverlineRule />
      {effectiveStatus === 'open' ? (
        <OpenState request={request} onSubmitted={() => setSubmitted(true)} />
      ) : effectiveStatus === 'answered' ? (
        <SubmittedState request={request} />
      ) : (
        <ClosedState request={request} />
      )}
    </div>
  );
}
